import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { createSlice } from '@reduxjs/toolkit'
import { getAdjustedIndexAndTimestamp } from '../components/Utilities';

dayjs.extend(customParseFormat);

const dataSlice = createSlice({
    name: 'data',
    initialState: {
        source: null,
        index: null,
        overViewCameraId: null,
        events: [],
        initialFooterHeight: 48,
        timestamp: dayjs(),
        download: { loading: false, progress: 0 },
        generation: { loading: false, progress: 0 },
        checkedArchivePictures: [],
        token: null,
        notification: {},
        isWeatherAlarmViaManagerMessage: false,
        subscriptionId: null,
        accessMonitoringCameraId: null,
        accessMonitoringVehicleFilter: null // null = show all, [] = show nothing, [...keys] = show only these
    },
    reducers: {
        setSource: (state, { payload }) => {
            state.source = payload;
            if (payload) {
                const timestamp = payload[0]?.[state.index]?.[0];
                let indexAndTimestamp = getAdjustedIndexAndTimestamp(payload, timestamp)
                state.index = indexAndTimestamp.index;
                state.timestamp = indexAndTimestamp.timestamp;
            }
        },
        setIndex: (state, { payload }) => {
            state.index = payload;
        },
        setTimestamp: (state, { payload }) => {

            let dayJsFromDate;

            if (/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/.test(payload)) { // check if payload is in "DD.MM.YYYY HH:mm" format
                dayJsFromDate = dayjs(payload, "DD.MM.YYYY HH:mm", true);
            } else { // payload is e.g. "Mon Jun 30 2025 13:45:00 GMT+0200 (Mitteleuropäische Sommerzeit)
                dayJsFromDate = dayjs(payload);
            }

            if (dayJsFromDate.isValid() && !state.timestamp.isSame(dayJsFromDate)) {
                state.timestamp = dayJsFromDate
                if (state.source && payload) {
                    let indexAndTimestamp = getAdjustedIndexAndTimestamp(state.source, dayJsFromDate)
                    state.index = indexAndTimestamp.index;
                    state.timestamp = indexAndTimestamp.timestamp;
                }
            }
        },
        setOverviewCameraId: (state, { payload }) => {
            state.overViewCameraId = payload;
        },
        setEvents: (state, { payload }) => {
            state.events = payload
        },
        setInitialFooterHeight: (state, { payload }) => {
            state.initialFooterHeight = payload
        },
        loadPrevImage: (state) => {
            const decrementedIndex = state.index - 1;
            if (state.source[0]?.[decrementedIndex]) {
                state.index = decrementedIndex;
                state.timestamp = dayjs(state.source[0][decrementedIndex][0]);
            }
        },
        loadNextImage: (state) => {
            const incrementedIndex = state.index + 1;
            if (state.source[0]?.[incrementedIndex]) {
                state.index = incrementedIndex;
                state.timestamp = dayjs(state.source[0][incrementedIndex][0]);
            }
        },
        setDownload: (state, { payload }) => {
            state.download = { ...state.download, ...payload }
        },
        setGeneration: (state, { payload }) => {
            state.generation = { ...state.generation, ...payload }
        },
        setCheckedArchivePictures: (state, { payload }) => {
            const type = payload.type
            const action = payload.action
            if (type === 'clean') {
                state.checkedArchivePictures = []
            } else if (type === 'add') {
                state.checkedArchivePictures.push(action.value)
            } else if (type === 'remove') {
                state.checkedArchivePictures.splice(action.index, 1)
            }
        },
        setToken: (state, { payload }) => {
            window.history.replaceState({}, "", window.location.pathname + window.location.search) // remove hash from URL
            state.token = payload
        },
        setNotification: (state, { payload }) => {
            state.notification = payload
        },
        resetNotification: (state) => {
            state.notification = {}
        },
        setIsWeatherAlarmViaManagerMessage: (state, { payload }) => {
            state.isWeatherAlarmViaManagerMessage = payload
        },
        setSubscriptionId: (state, { payload }) => {
            state.subscriptionId = payload
        },
        setAccessMonitoringCameraId: (state, { payload }) => {
            state.accessMonitoringCameraId = payload
        },
        setAccessMonitoringVehicleFilter: (state, { payload }) => {
            state.accessMonitoringVehicleFilter = payload
        }
    }
})

export const {
    setSource,
    setIndex,
    setTimestamp,
    setOverviewCameraId,
    setEvents,
    setInitialFooterHeight,
    loadPrevImage,
    loadNextImage,
    setDownload,
    setGeneration,
    setCheckedArchivePictures,
    setToken,
    setNotification,
    resetNotification,
    setIsWeatherAlarmViaManagerMessage,
    setSubscriptionId,
    setAccessMonitoringCameraId,
    setAccessMonitoringVehicleFilter
} = dataSlice.actions

export const getData = state => state.data

export default dataSlice.reducer